import React from 'react';
import {connect} from 'react-redux';
import GetAppIcon from '@material-ui/icons/GetApp';
import PrintIcon from '@material-ui/icons/Print';
import EditIcon from '@material-ui/icons/Edit';
import moment from 'moment';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router-dom';
import {
    Box,
    TablePagination,
    TextField,
    MenuItem,
    Button,
    Typography,
    Paper,
    CircularProgress,
    Tooltip,
    withStyles
} from '@material-ui/core';
import MaterialTable from 'material-table';

import Header from '../layout/Header';
import Breadcrumb from '../layout/Breadcrumb';
import ApiClient from '../api/ApiClient';
import PrintPdfDialog from '../components/PrintPdfDialog';

const API_URL = process.env.REACT_APP_API_URL;
const fileFormat = ['CSV', 'TSV', 'JSON', 'YAML', 'HTML'];

const styles = (theme) => ({
    main: {
        flexGrow: 1,
        padding: theme.spacing(2),
        position: 'relative'
    },
    paper: {
        width: '100%',
        overflowX: 'auto'
    },
    table: {
        minWidth: 650
    },
    title: {
        padding: theme.spacing(2, 0, 0, 2)
    },
    exportContainer: {
        position: 'absolute',
        zIndex: 10,
        right: '350px',
        top: '25px'
    }
});

function CustomPagination(props) {
    const { onPageChange, onRowsPerPageChange, ...rest } = props;
    return (
        <TablePagination
            {...rest}
            onChangePage={onPageChange || props.onChangePage}
            onChangeRowsPerPage={onRowsPerPageChange || props.onChangeRowsPerPage}
        />
    );
}

class Dashboard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            exportFormat: 'CSV',
            isExporting: false,
            selectedDevices: [],
            isPrintDialogOpen: false,
            printTemplateGroups: []
        };
        this.handleExport = this.handleExport.bind(this);
    }

    componentDidMount() {
        ApiClient.fetchJson('devices/lists/', 'GET', {}, (response) => {
            this.setState({ printTemplateGroups: response.print_template_groups });
        });
    }

    getAuthToken() {
        const root = JSON.parse(localStorage.getItem('persist:root'));
        const auth = JSON.parse(root.auth);
        return auth.accessToken;
    }

    async handleExport() {
        this.setState({isExporting: true});
        try {
            const token = this.getAuthToken();
            const {selectedDevices} = this.state;
            let exportUrl = `${API_URL}devices/export/?export_format=${this.state.exportFormat}`;
            if (selectedDevices.length > 0) {
                exportUrl += `&device_ids=${selectedDevices.map((d) => d.device_id).join(',')}`;
            }

            const response = await fetch(exportUrl,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) throw new Error('Export failed');
            const disposition = response.headers.get('Content-Disposition');
            let filename;
            if (disposition && disposition.includes('filename')) {
                const filenameIndex = disposition.indexOf('filename=');
                if (filenameIndex !== -1) {
                    filename = disposition
                        .substring(filenameIndex + 9)
                        .split(';')[0]
                        .replace(/['"]/g, '')
                        .trim();
                }
            }

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export data');
        } finally {
            this.setState({isExporting: false});
        }
    }

    render() {
        const {classes, history} = this.props;
        const {exportFormat, isExporting, selectedDevices, isPrintDialogOpen, printTemplateGroups} = this.state;
        return (
            <div>
                <Header/>
                <Breadcrumb>
                    <Typography color="textPrimary">Devices</Typography>
                </Breadcrumb>
                <main className={classes.main}>
                    <Paper className={classes.paper}>
                        <Box className={classes.exportContainer}>
                            <Tooltip title="Print selected">
                                <span>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => this.setState({isPrintDialogOpen: true})}
                                        disabled={selectedDevices.length === 0}
                                        style={{
                                            height: '40px',
                                            minWidth: '40px',
                                            marginRight: '8px'
                                        }}
                                    >
                                        <PrintIcon/>
                                    </Button>
                                </span>
                            </Tooltip>
                            <TextField
                                select
                                value={exportFormat}
                                onChange={(e) =>
                                    this.setState({exportFormat: e.target.value})
                                }
                                variant="outlined"
                                size="small"
                                className={classes.formControl}
                                disabled={isExporting}
                            >
                                {fileFormat.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Tooltip title="Download">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.handleExport}
                                    disabled={isExporting || selectedDevices.length === 0}
                                    style={{
                                        height: '40px',
                                        minWidth: '40px',
                                        marginLeft: '2px'
                                    }}
                                >
                                    {isExporting ? (
                                        <CircularProgress size={24} color="inherit"/>
                                    ) : (
                                        <GetAppIcon/>
                                    )}
                                </Button>
                            </Tooltip>
                        </Box>
                        <MaterialTable
                            columns={[
                                {title: 'Serial number', field: 'serial_number'},
                                {title: 'External serial number', field: 'ext_serial_number'},
                                {title: 'Model', field: 'model'},
                                {title: 'Init date', field: 'init_date'},
                                {
                                    title: 'Warehouse',
                                    field: 'portal_warehouse_code',
                                    render: (rowData) => rowData.warehouse_sent_at
                                        ? (rowData.portal_warehouse_name || rowData.portal_warehouse_code || '—')
                                        : '—',
                                },
                                {
                                    title: 'Actions',
                                    field: '_actions',
                                    sorting: false,
                                    searchable: false,
                                    render: (rowData) => (
                                        <Tooltip title="Edit">
                                            <Button
                                                size="small"
                                                onClick={() => history.push(`/device/offline/${rowData.device_id}`)}
                                            >
                                                <EditIcon fontSize="small"/>
                                            </Button>
                                        </Tooltip>
                                    )
                                }
                            ]}
                            components={{
                                Pagination: CustomPagination
                            }}
                            options={{
                                pageSize: 10,
                                selection: true
                            }}
                            data={(query) =>
                                new Promise((resolve) => {
                                    let url = `devices?page=${query.page + 1}&page_size=${
                                        query.pageSize
                                    }`;
                                    if (query.search && query.search !== '')
                                        url += `&search=${query.search}`;
                                    if (query.orderBy) {
                                        let order = query.orderDirection === 'asc' ? '' : '-';
                                        order += query.orderBy.field;
                                        url += `&ordering=${order}`;
                                    }
                                    ApiClient.fetchJson(url, 'GET', {}, (response) => {
                                        let data = [];
                                        response.results.forEach((element) => {
                                            element.init_date = element.init
                                                ? moment(element.init_date).format('DD.MM.YYYY HH:mm')
                                                : '';
                                            data.push(element);
                                        });

                                        resolve({
                                            data,
                                            page: response.page - 1,
                                            totalCount: response.count
                                        });
                                    });
                                })
                            }
                            title="All devices"
                            onSelectionChange={(rows) =>
                                this.setState({selectedDevices: rows})
                            }
                        />
                    </Paper>
                    <PrintPdfDialog
                        open={isPrintDialogOpen}
                        onClose={() => this.setState({isPrintDialogOpen: false})}
                        deviceIds={selectedDevices.map((d) => d.device_id)}
                        printTemplateGroups={printTemplateGroups}
                        defaultFilename="devices.pdf"
                    />
                </main>
            </div>
        );
    }
}

Dashboard.propTypes = {
    classes: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
    isTokenRefreshing: state.auth.isTokenRefreshing,
    devices: state.devices.devices
});

export default connect(mapStateToProps)(
    withRouter(withStyles(styles)(Dashboard))
);
